<script setup lang="ts">
    import { ref, computed, toRef } from 'vue'
    import { useRoute } from 'vue-router'

    import { strCompareLowerCase } from '@common/Common'
    import { dynamicUiRoutes } from '../../../App/Web/Scripts/dynamicUiRoutes'

    import type { AlternativeReadModel } from '@Alternatives/Alternatives/Domain/readmodels'
    import type { AlternativeRelationshipsDialogViewModel } from '@Alternatives/Alternatives/Components/AlternativeRelationshipsDialog'

    // ---------------- props / emits ----------------

    const props = defineProps<{
        viewModel: AlternativeRelationshipsDialogViewModel
    }>()

    const emit = defineEmits<{
        (e: 'load-alternatives'): void
        (e: 'unlink-dependent', id: string): void
        (e: 'unlink-foundation', id: string): void
        (e: 'link-dependent', id: string): void
        (e: 'link-foundation', id: string): void
    }>()

    // ---------------- routing ----------------

    const route = useRoute()

    // ---------------- shortcuts ----------------

    const vm = toRef(props, 'viewModel')

    // ---------------- local state ----------------

    const searchOtherPhrase = ref<string | null>(null)
    const tab = ref<'Foundations' | 'Dependents'>('Foundations')

    // ---------------- derived (VM passthrough) ----------------

    const opened = computed(() => vm.value?.opened ?? false)

    const workspaceId = computed(() => vm.value.workspaceId)
    const tchProjectId = computed(() => vm.value.tchProjectId)

    const alternativeLabel = computed(() => vm.value.alternativeLabel)
    const alternativesLabel = computed(() => vm.value.alternativesLabel)

    const alternativeId = computed(() => vm.value.alternativeId)
    const alternativeName = computed(() => vm.value.alternativeName)
    const alternativeStatusId = computed(() => vm.value.alternativeStatusId)
    const alternativeStatusName = computed(() => vm.value.alternativeStatusName)

    const dependents = computed(() => vm.value.dependents)
    const foundations = computed(() => vm.value.foundations)

    // ---------------- helpers ----------------

    function url_AlternativeDashboardPage(alternativeId: string): string
    {
        return dynamicUiRoutes.Alternatives_AlternativeDashboardPage(
            route,
            workspaceId.value,
            tchProjectId.value,
            alternativeId
        )
    }

    // ---------------- candidates ----------------

    const dependentsCandidatesView = computed<AlternativeReadModel[]>(() =>
    {
        const currIds = new Set([
            ...dependents.value.map(a => a.Id),
            alternativeId.value
        ])

        let view = vm.value.allAlternatives?.filter(a => !currIds.has(a.Id)) ?? []

        if (searchOtherPhrase.value)
        {
            const phrase = searchOtherPhrase.value.toLowerCase()
            view = view.filter(a =>
                a.Name && a.Name.toLowerCase().includes(phrase)
            )
        }

        return view.sort((a, b) => strCompareLowerCase(a.Name, b.Name))
    })

    const foundationsCandidatesView = computed<AlternativeReadModel[]>(() =>
    {
        const currIds = new Set([
            ...foundations.value.map(a => a.Id),
            alternativeId.value
        ])

        let view = vm.value.allAlternatives?.filter(a => !currIds.has(a.Id)) ?? []

        if (searchOtherPhrase.value)
        {
            const phrase = searchOtherPhrase.value.toLowerCase()
            view = view.filter(a =>
                a.Name && a.Name.toLowerCase().includes(phrase)
            )
        }

        return view.sort((a, b) => strCompareLowerCase(a.Name, b.Name))
    })

    // ---------------- actions ----------------

    function loadAlternatives()
    {
        if (vm.value.allAlternatives.length === 0)
        {
            emit('load-alternatives')
        }
    }

    function handleUnlinkDependent(d: AlternativeReadModel)
    {
        emit('unlink-dependent', d.Id)
    }

    function handleUnlinkFoundation(f: AlternativeReadModel)
    {
        emit('unlink-foundation', f.Id)
    }

    function handleLinkFoundation(f: AlternativeReadModel)
    {
        emit('link-foundation', f.Id)
    }

    function handleLinkDependent(d: AlternativeReadModel)
    {
        emit('link-dependent', d.Id)
    }

    function close()
    {
        vm.value.close()
    }
</script>


<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-lg modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Dependencies</h5>
                        <button @click="close" type="button" class="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <p>{{alternativeLabel}}: <a target="_blank" v-sanitizedHref="url_AlternativeDashboardPage(alternativeId)">{{alternativeName}}</a></p>
                        <p>Status: {{alternativeStatusName}}</p>
                        <br />

                        <ul class="nav nav-tabs" role="tablist">
                            <li class="nav-item">
                                <a class="nav-link" @click="tab = 'Foundations'" :class="{ active: tab === 'Foundations' }">
                                    Foundations ({{foundations.length}})
                                </a>
                            </li>
                            <li class="nav-item">
                                <a class="nav-link" @click="tab = 'Dependents'" :class="{ active: tab === 'Dependents' }">
                                    Dependents ({{dependents.length}})
                                </a>
                            </li>
                        </ul>
                        <br />

                        <div class="tab-content" style="height: 400px;">
                            <div class="tab-pane" :class="{ active: tab === 'Foundations' }">
                                <div>
                                    This {{alternativeLabel.toLowerCase()}} is dependent on {{foundations.length}} other {{alternativesLabel.toLowerCase()}}

                                    <div class="dropdown d-inline-block">
                                        <a class="text-primary" data-toggle="dropdown" @click="loadAlternatives">Add a foundation</a>
                                        <div class="dropdown-menu">
                                            <form class="px-2 py-1">
                                                <div class="form-group">
                                                    <input type="text" class="form-control" placeholder="Search..." v-model="searchOtherPhrase" />
                                                </div>
                                            </form>
                                            <div style="max-height: 200px; overflow-y: auto;">
                                                <a class="dropdown-item" v-for="a in foundationsCandidatesView" :key="a.Id" @click="handleLinkFoundation(a)">
                                                    {{a.Name}}
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <table class="table table-hover mt-3" v-if="foundations.length > 0">
                                    <thead>
                                        <tr>
                                            <th>{{alternativeLabel}}</th>
                                            <th>Status</th>
                                            <th style="width: 100px;"></th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        <tr v-for="f in foundations" :key="f.Id" :class="{'table-warning': (f.StatusId !== alternativeStatusId) }">
                                            <td class="align-middle">
                                                <a target="_blank" v-sanitizedHref="url_AlternativeDashboardPage(f.Id)">{{f.Name}}</a>
                                            </td>
                                            <td class="align-middle">{{f.StatusName}}</td>
                                            <td class="align-middle">
                                                <button class="btn btn-sm btn-outline-secondary" @click="handleUnlinkFoundation(f)" title="Remove dependency">
                                                    <i class="fa fa-chain-broken" aria-hidden="true"></i>
                                                </button>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <div class="tab-pane" :class="{ active: tab === 'Dependents' }">
                                <div>
                                    <span>
                                        This {{alternativeLabel.toLowerCase()}} is a foundation for {{dependents.length}} other {{alternativesLabel.toLowerCase()}}.
                                    </span>

                                    <div class="dropdown d-inline-block">
                                        <a class="text-primary" data-toggle="dropdown" @click="loadAlternatives">Add a dependent</a>
                                        <div class="dropdown-menu">
                                            <form class="px-2 py-1">
                                                <div class="form-group">
                                                    <input type="text" class="form-control" placeholder="Search..." v-model="searchOtherPhrase" />
                                                </div>
                                            </form>
                                            <div style="max-height: 200px; overflow-y: auto;">
                                                <a class="dropdown-item" v-for="a in dependentsCandidatesView" :key="a.Id" @click="handleLinkDependent(a)">
                                                    {{a.Name}}
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <table class="table table-hover mt-3" v-if="dependents.length > 0">
                                    <thead>
                                        <tr>
                                            <th>{{alternativeLabel}}</th>
                                            <th>Status</th>
                                            <th style="width: 100px;"></th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        <tr v-for="d in dependents" :key="d.Id" :class="{'table-warning': (d.StatusId !== alternativeStatusId) }">
                                            <td class="align-middle">
                                                <a target="_blank" v-sanitizedHref="url_AlternativeDashboardPage(d.Id)">{{d.Name}}</a>
                                            </td>
                                            <td class="align-middle">{{d.StatusName}}</td>
                                            <td>
                                                <button class="btn btn-sm btn-outline-secondary" @click="handleUnlinkDependent(d)" title="Remove dependency">
                                                    <i class="fa fa-chain-broken" aria-hidden="true"></i>
                                                </button>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                    </div>

                    <div class="modal-footer">
                        <button @click="close" type="button" class="btn btn-outline-secondary">Close</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>